import type { AtribuicaoAnuncio } from './TrafegoPagoService';
import { apiFetch } from './api';

const apiUrl = import.meta.env.VITE_API_URL;

export interface DetalheConversa {
    idDetalhe: number;
    idConversa: number;
    descricaoDetalhe: string;
    dataDetalheCriado: string;
    idUsuarioCriador: number;
    nomeUsuarioCriador: string;
}

export interface Opportunity {
    idConversa: number;
    idContato: number;
    nomeContato: string;
    numeroWhatsapp: string;
    dataConversaCriada: string;
    dataFechamentoVenda: string | null;
    /**
     * Quando foi a última mensagem da conversa, em qualquer direção — o que sustenta o filtro
     * "Sem interação". Nulo quando não há mensagem nenhuma (negócio criado à mão) e ausente
     * enquanto a API publicada for anterior a 13/10/2026.
     */
    dataUltimaInteracao?: string | null;
    /**
     * Quem mandou a última mensagem (0 cliente, 1 vendedor, 2 IA); nulo sem mensagem nenhuma e
     * ausente enquanto a API publicada for anterior a 14/10/2026. É o que separa, no Painel
     * Operacional, quem está ESPERANDO resposta de quem já foi respondido e sumiu — "parado há N
     * dias" junta os dois. Mesmo significado do `ultimaMensagemOrigem` da lista do Chat.
     */
    ultimaInteracaoOrigem?: number | null;
    idStauts: number;
    descricaoStatus: string;
    nomeUsuarioResponsavel: string;
    idUsuarioResponsavel?: number;
    valor: number;
    detalhesConversa: DetalheConversa[];
    campanha?: boolean;
    idCampanha?: number | null;
    nomeCampanha?: string;
    /** De qual ANÚNCIO veio. Diferente de `campanha`/`idCampanha`, que são disparo. */
    atribuicaoAnuncio?: AtribuicaoAnuncio | null;
    tipoLeadId?: number;
    idOrigemOportunidade?: number;
    descricaoOrigemOportunidade?: string;
    /**
     * De ONDE a pessoa veio, quando a porta de entrada soube dizer (RD Station, webhook, página de
     * captura). `descricaoOrigemOportunidade` acima é o CANAL — onde se conversa. Nulo para quem
     * chegou sem passar por porta rastreada, e aí "Entrada orgânica" é verdade.
     */
    origemMarketing?: OrigemMarketing | null;
    /** Organização do negócio — coluna própria, independente do contato (Organizações, fase 2). */
    idOrganizacao?: number | null;
    nomeOrganizacao?: string;
    /** O contato está fora do funil: a conversa só aparece no Chat e na ficha do contato. */
    foraDoFunil?: boolean;
    /** Por que está fora do funil. Hoje só 'contato'. */
    foraDoFunilMotivo?: string | null;
    /** Tipo do contato (lead, fornecedor…); nulo = sem tipo. A ficha resolve o nome pela lista de tipos. */
    tipoContatoId?: number | null;
}

/** A origem de marketing de um negócio. Ver `origemMarketing`. */
export interface OrigemMarketing {
    /** Rótulo pronto da plataforma: "Busca Paga | google". */
    origem?: string | null;
    /** A porta: nome da conversão ou do formulário. */
    conversao?: string | null;
    utmSource?: string | null;
    utmMedium?: string | null;
    utmCampaign?: string | null;
}

/**
 * Uma levantada de mão: a pessoa procurando a empresa. Já é o GESTO — os sinais da mesma
 * janela de 30 min chegam fundidos numa linha só, com o mais rico completando o mais pobre.
 */
export interface HandRaise {
    /** A hora do gesto, no relógio da fonte (não a do processamento). */
    quando: string;
    /** Canal por onde entrou, no mesmo código de `idOrigemOportunidade`. */
    idCanal: number;
    /** A porta, como a origem a nomeou: "whatsapp-bold-workplace". Nulo = chegou direto. */
    conversao?: string | null;
    /** "Busca Paga | google". Nulo = sem origem registrada. */
    origem?: string | null;
    utmSource?: string | null;
    utmMedium?: string | null;
    utmCampaign?: string | null;
    /** Disparo respondido. */
    idCampanha?: number | null;
    nomeCampanha?: string | null;
    /** O negócio em que o gesto caiu — a lista é do contato e atravessa negócios. */
    idConversa?: number | null;
    /** "lead", "mensagem" ou "backfill". */
    tipo: string;
}

export interface OpportunityResponse {
    sucesso: boolean;
    mensagem: string;
    dados: Opportunity[];
}

export interface MotivoPerdaHistorico {
    idMotivoPerda: number;
    idConversa: number;
    descricaoMotivo: string;
    detalhesMotivo: string;
    dataMotivo: string;
}

export interface CreateOpportunityCampoPersonalizado {
    campoPersonalizadoId: number;
    valor: string | null;
}

export interface CreateOpportunityPayload {
    contatoNovo: boolean;
    contatoId: number | null;
    contato: {
        nome: string;
        telefone?: string;
        email?: string;
    } | null;
    usuarioResponsavelId: number | null;
    tipoLeadId: number | null;
    valor: number | null;
    observacao: string | null;
    camposPersonalizados?: CreateOpportunityCampoPersonalizado[] | null;
    /** Etapa em que a oportunidade nasce (2, 4 ou 5). Sem ele a API usa Em qualificação. */
    statusId?: number | null;
    /** Organização do negócio; com ela o contato passa a ser opcional. */
    organizacaoId?: number | null;
}

export const OpportunityService = {
    createOpportunity: async (payload: CreateOpportunityPayload): Promise<{ sucesso: boolean; mensagem: string; dados: number | null }> => {
        const response = await apiFetch(`${apiUrl}/api/OportunidadeVenda/Criar`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        return response.json();
    },

    /** Troca ou desfaz (null) a organização do negócio. A API vincula também o contato que ainda não tem uma. */
    atualizarOrganizacao: async (idConversa: number, organizacaoId: number | null): Promise<{ sucesso: boolean; mensagem: string }> => {
        const response = await apiFetch(`${apiUrl}/api/OportunidadeVenda/${idConversa}/Organizacao`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ organizacaoId, vincularContato: true }),
        });
        const corpo = await response.json().catch(() => null);
        if (!response.ok || corpo?.sucesso === false) {
            throw new Error(corpo?.mensagem || 'Não foi possível atualizar a organização.');
        }
        return corpo;
    },

    /** `incluirForaDoFunil` só a ficha do contato pede: o resto do app não enxerga conversa de contato fora do funil. */
    getAllOpportunities: async (opcoes?: { incluirForaDoFunil?: boolean }): Promise<Opportunity[]> => {
        try {
            const consulta = opcoes?.incluirForaDoFunil ? '?incluirForaDoFunil=true' : '';
            const response = await apiFetch(`${apiUrl}/api/OportunidadeVenda/BuscarTodas${consulta}`, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                if (response.status === 400) {
                    return [];
                }
                throw new Error('Erro ao buscar oportunidades');
            }

            const data: OpportunityResponse = await response.json();

            if (data.sucesso) {
                return data.dados;
            } else {
                console.error(data.mensagem);
                return [];
            }
        } catch (error) {
            console.error('Erro no servico de oportunidades:', error);
            throw error;
        }
    },

    /**
     * Um negócio pelo id. A lista (`BuscarTodas`) é o recorte do quadro — campanha
     * só aparece depois que o cliente responde (até 27/09 também cortava em 30
     * dias) —, então procurar nela quem chega por tarefa ou link dava "não encontrada".
     * Devolve `null` quando o negócio não existe ou não é de quem pede. Se a API
     * ainda não tem a rota (front publicado antes dela), cai na lista, como antes.
     */
    getOpportunityById: async (idConversa: number): Promise<Opportunity | null> => {
        const response = await apiFetch(`${apiUrl}/api/OportunidadeVenda/BuscarPorId/${idConversa}`);
        const corpo = await response.json().catch(() => null);
        if (corpo && typeof corpo.sucesso === 'boolean') {
            return corpo.sucesso ? corpo.dados : null;
        }
        if (response.status === 404) {
            const todas = await OpportunityService.getAllOpportunities();
            return todas.find(o => o.idConversa === idConversa) ?? null;
        }
        throw new Error('Erro ao buscar a oportunidade');
    },

    /**
     * As levantadas de mão da PESSOA — cada vez que ela procurou a empresa (conversão do
     * site, anúncio, resposta a disparo, a primeira mensagem dela numa conversa). A lista é
     * do contato e atravessa negócios, por isso a API a entrega a partir do id da conversa,
     * que é o que ela consegue conferir de quem pede. Sinais do mesmo gesto já vêm fundidos.
     * Lista vazia = ninguém levantou a mão ainda (negócio que a equipe cadastrou).
     */
    getHandRaises: async (idConversa: number): Promise<HandRaise[]> => {
        try {
            const response = await apiFetch(`${apiUrl}/api/OportunidadeVenda/LevantadasDeMao/${idConversa}`);
            const corpo = await response.json().catch(() => null);
            return corpo?.sucesso && Array.isArray(corpo.dados) ? corpo.dados : [];
        } catch (error) {
            console.error('Erro ao buscar as levantadas de mão:', error);
            return [];
        }
    },

    /** Edita o texto de uma anotação. A API só aceita do autor e recusa as do sistema. */
    updateDetail: async (idDetalhe: number, descricao: string): Promise<void> => {
        const response = await apiFetch(`${apiUrl}/api/Detalhes/${idDetalhe}`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ descricao }),
        });
        const corpo = await response.json().catch(() => null);
        if (!response.ok || corpo?.sucesso === false) throw new Error(corpo?.mensagem || 'Não foi possível salvar a anotação.');
    },

    /** Prende à anotação anexos que já existem na conversa — a imagem colada antes de a anotação ter id. */
    linkAttachmentsToDetail: async (idDetalhe: number, anexoIds: number[]): Promise<void> => {
        if (anexoIds.length === 0) return;
        const response = await apiFetch(`${apiUrl}/api/Detalhes/${idDetalhe}/Anexos`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ anexoIds }),
        });
        const corpo = await response.json().catch(() => null);
        if (!response.ok || corpo?.sucesso === false) throw new Error(corpo?.mensagem || 'Não foi possível vincular os anexos.');
    },

    /** Exclui uma anotação (só o autor). Os anexos dela ficam na oportunidade. */
    deleteDetail: async (idDetalhe: number): Promise<void> => {
        const response = await apiFetch(`${apiUrl}/api/Detalhes/${idDetalhe}`, { method: 'DELETE' });
        const corpo = await response.json().catch(() => null);
        if (!response.ok || corpo?.sucesso === false) throw new Error(corpo?.mensagem || 'Não foi possível excluir a anotação.');
    },

    addDetails: async (details: { idConversa: number, descricao: string, statusNegociacaoId: number, valor: number }): Promise<{ sucesso: boolean, mensagem?: string, dados?: number | null }> => {
        try {
            const response = await apiFetch(`${apiUrl}/api/Detalhes/Adicionar`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(details)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.mensagem || 'Erro ao adicionar detalhes');
            }

            // A API devolve o id da anotação criada — é o que prende os anexos a ela.
            const corpo = await response.json().catch(() => null);
            return { sucesso: true, dados: typeof corpo?.dados === 'number' ? corpo.dados : null };
        } catch (error: any) {
            console.error('Erro ao adicionar detalhes:', error);
            return { sucesso: false, mensagem: error.message };
        }
    },

    getMotivosPerdaHistorico: async (idConversa: number): Promise<MotivoPerdaHistorico[]> => {
        try {
            const response = await apiFetch(`${apiUrl}/api/OportunidadeVenda/MotivosPerda/${idConversa}`, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                if (response.status === 400) return [];
                throw new Error('Erro ao buscar histórico de motivos de perda');
            }

            const data = await response.json();
            if (data.sucesso && Array.isArray(data.dados)) {
                return data.dados;
            }
            return [];
        } catch (error) {
            console.error('Erro ao buscar histórico de motivo de perda:', error);
            return [];
        }
    }
};
